import { ThemeProvider } from '@speaking-coach/ui'
import React from 'react'
import ReactDOM from 'react-dom/client'
import { Provider as JotaiProvider } from 'jotai'
import { QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider, createBrowserRouter } from 'react-router-dom'
import { App } from './App'
import { createAdminQueryClient } from './api/query-client'
import { browserSentryDsn } from '@speaking-coach/shared-types'
import { runtimeEnvironment } from './config/runtime-environment'
import { initSentryBrowser } from './telemetry/sentry'
import '@speaking-coach/ui/fonts.css'
import './styles.css'

// Env-gated Sentry (ADR-0046). No-op unless the DSN is nonblank and the
// environment is exactly staging or production; otherwise admin errors report
// to the shared coach-web Sentry project tagged app=admin. The staging build
// inlines VITE_SENTRY_DSN; the environment comes from the page's own host
// (ADR-0084), and staging and production both report (ADR-0089).
const environment = runtimeEnvironment()
initSentryBrowser({
  dsn: browserSentryDsn(import.meta.env.VITE_SENTRY_DSN),
  environment: environment.name,
  release: import.meta.env.VITE_APP_VERSION || undefined,
  apiBaseUrl: environment.apiBaseUrl,
})

/**
 * Server state (ADR-0056). Sits beside JotaiProvider, which keeps the
 * session and the layout preference — the split is deliberate: Jotai owns
 * "who is this operator", TanStack Query owns "what does the server say".
 * Defaults + the mutation-success activity invalidation live in
 * `api/query-client.ts`.
 */
const queryClient = createAdminQueryClient()

const root = document.getElementById('root')
if (!root) {
  throw new Error('Root element not found')
}

/**
 * A DATA router (not `<BrowserRouter>`) because `useBlocker` — the hook
 * behind `common/unsaved-changes.tsx` — only registers with one. The route
 * tree stays where it was: a single splat route renders `App`, whose
 * descendant `<Routes>` keep matching as before, so this changes the
 * router's capabilities without moving any routes.
 */
const router = createBrowserRouter([{ path: '*', element: <App /> }])

ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <ThemeProvider storageKey="admin:theme">
      <QueryClientProvider client={queryClient}>
        <JotaiProvider>
          <RouterProvider router={router} />
        </JotaiProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </React.StrictMode>,
)
